import { create } from 'zustand';
import { checkForUpdate, getSkippedVersion, markSeen, getSeenVersion, platform, type UpdateInfo } from '@/lib/updater';
import { useToasts } from './toasts';

interface UpdaterStore {
  /** Resolved update info, or null when no update / not yet checked. */
  update: UpdateInfo | null;
  /** Whether the modal dialog is open. Desktop only. */
  dialogOpen: boolean;
  /** Hook called once on app boot. Branches Android vs desktop UX. */
  init: () => Promise<void>;
  /** Manually open the dialog (e.g. from the IconRail badge click). */
  openDialog: () => void;
  /** Close the dialog without taking action ("Later"). */
  closeDialog: () => void;
}

export const useUpdater = create<UpdaterStore>((set, get) => ({
  update: null,
  dialogOpen: false,
  init: async () => {
    try {
      const update = await checkForUpdate();
      if (!update) return;
      // Honor "skip this version" — quietly drop until a NEWER version ships.
      const skipped = getSkippedVersion();
      if (skipped === update.version) return;

      set({ update });

      const seen = getSeenVersion();
      const firstSeenThisVersion = seen !== update.version;

      // Desktop: auto-pop dialog on first detect for this version. After that,
      // user can re-open from the IconRail badge.
      // Android: push a toast (one-time per version), then the badge stays.
      if (platform() === 'desktop' && firstSeenThisVersion) {
        set({ dialogOpen: true });
      } else if (platform() === 'android' && firstSeenThisVersion) {
        useToasts.getState().push({
          tone: 'info',
          title: `Console ${update.version} available`,
          body: 'Tap Download to install. You may need to allow installs from unknown sources.',
          durationMs: 12_000,
        });
      }

      markSeen(update.version);
    } catch (e) {
      // Updater failure is never fatal — the app keeps running.
      // eslint-disable-next-line no-console
      console.warn('updater init failed', e);
    }
  },
  openDialog: () => {
    if (get().update) set({ dialogOpen: true });
    else if (platform() === 'android') {
      const u = get().update;
      if (u) void u.install(); // open APK URL in browser
    }
  },
  closeDialog: () => set({ dialogOpen: false }),
}));
